import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, X, Phone, Mail, MapPin, Building2, Calendar, Download, RefreshCw } from "lucide-react";
import { adminClient } from "@/lib/apiClient";
import { COMMERCIAL_INQUIRY_STATUSES } from "@/data/commercialContent";

const STATUS_TONE = {
  slate: "bg-slate-200 text-slate-700",
  amber: "bg-amber-100 text-amber-700",
  sky: "bg-sky-100 text-sky-700",
  violet: "bg-violet-100 text-violet-700",
  indigo: "bg-indigo-100 text-indigo-700",
  emerald: "bg-emerald-100 text-emerald-700",
  red: "bg-red-100 text-red-700",
  gray: "bg-gray-200 text-gray-600",
};

function StatusPill({ status }) {
  const meta = COMMERCIAL_INQUIRY_STATUSES.find((s) => s.slug === status) || COMMERCIAL_INQUIRY_STATUSES[0];
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${STATUS_TONE[meta.tone] || STATUS_TONE.gray}`}>
      {meta.label}
    </span>
  );
}

function InquiryDrawer({ inquiry, onClose, onStatusChange }) {
  if (!inquiry) return null;
  const fields = [
    ["Company", inquiry.company_name],
    ["Property Address", inquiry.property_address],
    ["City / Service Area", inquiry.city],
    ["Property Type", inquiry.property_type],
    ["Service Needed", inquiry.service_needed],
    ["Project Type", inquiry.project_type],
    ["Timeline", inquiry.timeline],
    ["Occupied / Vacant", inquiry.occupied_status],
    ["Full GC or Single Division", inquiry.gc_or_division],
  ].filter(([, v]) => v && String(v).trim());

  return (
    <div className="fixed inset-0 z-[150] bg-br-black/70 backdrop-blur-sm flex items-stretch justify-end" role="dialog" aria-modal="true" data-testid="commercial-inquiry-drawer">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-br-paper">
          <div>
            <div className="br-eyebrow text-br-bronze">Commercial Inquiry</div>
            <h3 className="font-display text-xl tracking-widest uppercase mt-1">{inquiry.contact_name}</h3>
            {inquiry.company_name && <div className="text-xs text-gray-600 mt-0.5">{inquiry.company_name}</div>}
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-br-black" aria-label="Close" data-testid="commercial-drawer-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-700"><Phone size={14} className="text-br-bronze" /> {inquiry.phone}</div>
            <div className="flex items-center gap-2 text-gray-700"><Mail size={14} className="text-br-bronze" /> {inquiry.email}</div>
            {inquiry.city && <div className="flex items-center gap-2 text-gray-700"><MapPin size={14} className="text-br-bronze" /> {inquiry.city}</div>}
            <div className="flex items-center gap-2 text-gray-700"><Calendar size={14} className="text-br-bronze" /> {new Date(inquiry.created_at).toLocaleString()}</div>
          </div>

          <div className="bg-br-paper border border-gray-200 rounded p-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Status</div>
            <div className="flex flex-wrap gap-1.5" data-testid="commercial-status-row">
              {COMMERCIAL_INQUIRY_STATUSES.map((s) => {
                const isActive = inquiry.status === s.slug;
                return (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => onStatusChange(inquiry, s.slug)}
                    className={`px-3 py-1 rounded text-[10px] uppercase tracking-widest font-semibold border transition ${isActive ? "bg-br-bronze border-br-bronze text-white" : "bg-white border-gray-200 text-gray-700 hover:border-br-bronze"}`}
                    data-testid={`commercial-status-${s.slug}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            {fields.map(([k, v]) => (
              <div key={k} className="bg-br-paper/40 border border-gray-100 rounded px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">{k}</div>
                <div className="text-sm text-gray-900 mt-0.5">{v}</div>
              </div>
            ))}
          </div>

          {inquiry.message && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">Message</div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{inquiry.message}</p>
            </div>
          )}

          {Array.isArray(inquiry.files) && inquiry.files.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Attached Files</div>
              <ul className="space-y-2">
                {inquiry.files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm bg-br-paper border border-gray-200 rounded px-3 py-2">
                    <Building2 size={14} className="text-br-bronze" />
                    <span className="truncate flex-1">{f.filename || `file-${i + 1}`}</span>
                    <a href={f.data_url} download={f.filename || `file-${i + 1}`} className="text-br-bronze hover:text-br-bronze-2 text-[11px] uppercase tracking-widest font-bold" data-testid={`commercial-file-${i}`}>
                      <Download size={12} className="inline" /> Download
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminCommercialInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", search: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      const res = await adminClient.get("/admin/commercial-inquiries", { params });
      setItems(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load commercial inquiries.");
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    if (!filters.search) return items;
    const q = filters.search.toLowerCase();
    return items.filter((a) =>
      (a.contact_name && a.contact_name.toLowerCase().includes(q)) ||
      (a.company_name && a.company_name.toLowerCase().includes(q)) ||
      (a.email && a.email.toLowerCase().includes(q)) ||
      (a.city && a.city.toLowerCase().includes(q))
    );
  }, [items, filters.search]);

  const openInquiry = async (inquiry) => {
    try {
      const res = await adminClient.get(`/admin/commercial-inquiries/${inquiry.id}`);
      setSelected(res.data);
    } catch (err) {
      setSelected(inquiry);
    }
  };

  const updateStatus = async (inquiry, status) => {
    try {
      await adminClient.patch(`/admin/commercial-inquiries/${inquiry.id}`, { status });
      setSelected((s) => (s && s.id === inquiry.id ? { ...s, status } : s));
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Could not update status.");
    }
  };

  return (
    <div data-testid="admin-commercial-inquiries">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="br-eyebrow">Commercial</div>
          <h2 className="font-display text-2xl tracking-widest uppercase mt-1">Commercial Inquiries</h2>
          <p className="text-sm text-gray-600 mt-1">Inquiries submitted from the public /commercial page.</p>
        </div>
        <button type="button" onClick={load} className="br-btn-dark text-[11px]" data-testid="commercial-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-testid="commercial-filters">
        <div>
          <label className="label-br">Search</label>
          <input type="text" className="input-br" placeholder="Contact, company, email, city" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="commercial-filter-search" />
        </div>
        <div>
          <label className="label-br">Status</label>
          <select className="select-br" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="commercial-filter-status">
            <option value="">All</option>
            {COMMERCIAL_INQUIRY_STATUSES.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4" data-testid="commercial-error">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500" data-testid="commercial-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading inquiries…
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-sm text-gray-500" data-testid="commercial-empty">No commercial inquiries match your filters.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-br-paper border-b border-gray-200">
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-gray-600">
                  <th className="px-4 py-3 font-bold">Contact</th>
                  <th className="px-4 py-3 font-bold">Company</th>
                  <th className="px-4 py-3 font-bold">Service</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map((a) => (
                  <tr key={a.id} className="hover:bg-br-paper/30 cursor-pointer" onClick={() => openInquiry(a)} data-testid={`commercial-row-${a.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-gray-900">{a.contact_name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{a.email} · {a.phone}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">{a.company_name || "—"}</td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">
                      <div>{a.service_needed || "—"}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{a.project_type || ""}</div>
                    </td>
                    <td className="px-4 py-3 align-top"><StatusPill status={a.status} /></td>
                    <td className="px-4 py-3 align-top text-[11px] text-gray-500">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <InquiryDrawer inquiry={selected} onClose={() => setSelected(null)} onStatusChange={updateStatus} />
    </div>
  );
}
